/**
 * Issue reports — the rules both sides of the form apply (04-mutations,
 * "Issue reports"). Pure, so the picker (client) and the Server Action share
 * one definition; the backend stays the authority and re-checks every one.
 */

export const TITLE_MAX = 200;
export const DESCRIPTION_MAX = 10_000;
export const COMMENT_MAX = 10_000;

/** How long a derived title may run before it is cut at a word. */
export const TITLE_LENGTH = 80;

/**
 * The form asks one question — what happened — and the report's required
 * `title` is its first line: whitespace collapsed, cut at a word boundary
 * with "…" past TITLE_LENGTH characters (a single over-long word is cut
 * mid-word). Empty only when the description is.
 */
export function titleFromDescription(description: string): string {
  const line =
    description
      .split(/\r?\n/)
      .map((l) => l.replace(/\s+/g, " ").trim())
      .find(Boolean) ?? "";
  if (line.length <= TITLE_LENGTH) return line;
  const cut = line.slice(0, TITLE_LENGTH - 1);
  const space = cut.lastIndexOf(" ");
  return (space > TITLE_LENGTH / 2 ? cut.slice(0, space) : cut).trimEnd() + "…";
}

/** Backend: at most 5 files per report, its comments' files included. */
export const MAX_FILES = 5;

/**
 * Backend: 10 MB per file. OURS: 10 MB per submission in total — the files
 * travel inside a Server Action, and `serverActions.bodySizeLimit` is 12 MB
 * (next.config.ts). A screenshot is well under a megabyte; five full-size
 * ones would be refused by Next before the backend ever saw them.
 */
export const MAX_SUBMISSION_BYTES = 10 * 1024 * 1024;

/** png, jpg, webp, gif, pdf, txt — the backend sniffs the bytes against the
 *  extension, so both are checked here. SVG and HTML are refused upstream. */
const ACCEPTED: Record<string, readonly string[]> = {
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/webp": ["webp"],
  "image/gif": ["gif"],
  "application/pdf": ["pdf"],
  "text/plain": ["txt"],
};

/** The file input's `accept`. */
export const FILE_ACCEPT = Object.values(ACCEPTED)
  .flat()
  .map((ext) => `.${ext}`)
  .join(",");

export type FileLike = { name: string; size: number; type: string };

export type FileProblem = "type" | "count" | "size";

/**
 * The first reason these files cannot be sent, or null. `already` is how many
 * files the report holds (its own and every comment's) — the backend's cap of
 * five counts them all.
 */
export function fileProblem(files: readonly FileLike[], already = 0): FileProblem | null {
  if (files.length + already > MAX_FILES) return "count";
  for (const f of files) {
    const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ACCEPTED[f.type]?.includes(ext)) return "type";
    if (f.size <= 0) return "type";
  }
  if (files.reduce((n, f) => n + f.size, 0) > MAX_SUBMISSION_BYTES) return "size";
  return null;
}

/**
 * The page the member came from, as handed over in `?from=` — kept only when it
 * is a same-origin PATH. Anything else (an absolute URL, `//host`, a backslash
 * trick, control characters) is dropped rather than repaired: it is a hint for
 * the editor, never worth a redirect-shaped hole.
 */
export function safeFromPath(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 2000) return null;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  return raw;
}

/**
 * A Sentry event id as the browser hands it over — 32 hex characters — or
 * null. It is a pointer for the team, so anything else is dropped, not fixed.
 */
export function safeEventId(raw: unknown): string | null {
  return typeof raw === "string" && /^[0-9a-f]{32}$/.test(raw) ? raw : null;
}

/**
 * `ReportIssueInput.context`: a JSON object string. The backend keeps only
 * these keys and cuts each to 1000 characters; empty values are left out.
 */
export function buildContext(ctx: {
  userAgent?: string | null;
  viewport?: string | null;
  build?: string | null;
  sentryEventId?: string | null;
}): string {
  const out: Record<string, string> = { app: "portal-fe" };
  for (const [k, v] of Object.entries(ctx)) {
    if (typeof v === "string" && v.trim()) out[k] = v.trim().slice(0, 1000);
  }
  return JSON.stringify(out);
}
